/**
 * Admin Events Management
 * Manage marketplace events (NOT yard sales - those are listings)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Calendar, Check, X, Eye, ExternalLink, MapPin,
  Loader2, RefreshCw, Clock, Users, Filter
} from 'lucide-react';
import { toast } from 'sonner';
import { useAdmin } from './AdminContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';

const AdminEvents = () => {
  const { apiCall, formatDate } = useAdmin();
  
  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending'); // pending, approved, rejected, all
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  
  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      const [eventsRes, categoriesRes] = await Promise.all([
        apiCall('get', filter === 'pending' 
          ? '/api/admin/events/pending'
          : `/api/events?status=${filter}&include_past=true`
        ),
        apiCall('get', '/api/events/categories')
      ]);
      
      setEvents(eventsRes.events || eventsRes || []);
      setCategories(categoriesRes.categories || []);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setLoading(false);
    }
  }, [apiCall, filter]);
  
  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);
  
  const handleApprove = async (eventId) => {
    try {
      setActionLoading(eventId);
      await apiCall('put', `/api/admin/events/${eventId}/approve`);
      toast.success('Event approved');
      fetchEvents();
    } catch (error) {
      console.error('Failed to approve event:', error);
    } finally {
      setActionLoading(null);
    }
  };
  
  const handleReject = async (eventId, reason = '') => {
    try {
      setActionLoading(eventId);
      await apiCall('put', `/api/admin/events/${eventId}/reject?reason=${encodeURIComponent(reason)}`);
      toast.success('Event rejected');
      fetchEvents();
      setSelectedEvent(null);
    } catch (error) {
      console.error('Failed to reject event:', error);
    } finally {
      setActionLoading(null);
    }
  };
  
  const handleDelete = async (eventId) => {
    try {
      setActionLoading(eventId);
      await apiCall('delete', `/api/admin/events/${eventId}`);
      toast.success('Event deleted');
      fetchEvents();
      setSelectedEvent(null);
    } catch (error) {
      console.error('Failed to delete event:', error);
    } finally {
      setActionLoading(null);
    }
  };
  
  const getCategoryInfo = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat || { name: categoryId, icon: '📅', color: '#6B7280' };
  };
  
  const isEventPast = (eventDate) => {
    return new Date(eventDate) < new Date();
  };
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Calendar className="w-6 h-6 text-primary" />
            Events Management
          </h2>
          <p className="text-sm text-muted-foreground">
            Review and manage marketplace events (parties, promotions, activities)
          </p>
        </div>
        <button
          onClick={fetchEvents}
          disabled={loading}
          className="p-2 rounded-lg bg-secondary hover:bg-secondary/80 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>
      
      {/* Info Box */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
        <p className="text-sm text-blue-600">
          <strong>Note:</strong> Yard Sales are managed under Listings, not Events. 
          Events are for parties, promotions, and community activities.
        </p>
      </div>
      
      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        {[
          { id: 'pending', label: 'Pending Approval' },
          { id: 'approved', label: 'Approved' },
          { id: 'rejected', label: 'Rejected' },
          { id: 'all', label: 'All' }
        ].map(f => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === f.id
                ? 'bg-primary text-primary-foreground'
                : 'bg-secondary hover:bg-secondary/80'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>
      
      {/* Events Grid */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground">No {filter !== 'all' ? filter : ''} events found</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {events.map(event => {
              const category = getCategoryInfo(event.category);
              const isPast = isEventPast(event.event_date);
              
              return (
                <div 
                  key={event.id}
                  className={`p-4 hover:bg-secondary/20 transition-colors ${isPast ? 'opacity-60' : ''}`}
                >
                  <div className="flex items-start gap-4">
                    {/* Event Image */}
                    <div className="w-24 h-24 rounded-lg bg-secondary overflow-hidden flex-shrink-0">
                      {event.image || event.images?.[0] ? (
                        <img 
                          src={event.image || event.images[0]} 
                          alt="" 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-3xl">
                          {category.icon}
                        </div>
                      )}
                    </div>
                    
                    {/* Event Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h3 className="font-semibold flex items-center gap-2">
                            {event.title}
                            {isPast && (
                              <span className="text-xs bg-gray-500/20 text-gray-500 px-1.5 py-0.5 rounded">
                                Past
                              </span>
                            )}
                          </h3>
                          <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground mt-1">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatDate(event.event_date)}
                            </span>
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {event.location?.island || 'Unknown'}
                            </span>
                            {event.interested_count !== undefined && (
                              <span className="flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                {event.interested_count + (event.going_count || 0)} interested
                              </span>
                            )}
                          </div>
                        </div>
                        <span 
                          className="px-2 py-1 rounded text-xs"
                          style={{ 
                            backgroundColor: `${category.color}20`, 
                            color: category.color 
                          }}
                        >
                          {category.icon} {category.name}
                        </span>
                      </div>
                      
                      <p className="text-sm text-muted-foreground line-clamp-2 mt-2">
                        {event.description}
                      </p>
                      
                      {/* Status & Actions */}
                      <div className="flex items-center gap-2 mt-3">
                        {event.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleApprove(event.id)}
                              disabled={actionLoading === event.id}
                              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50"
                            >
                              {actionLoading === event.id ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <Check className="w-3 h-3" />
                              )}
                              Approve
                            </button>
                            <button
                              onClick={() => setSelectedEvent({ ...event, action: 'reject' })}
                              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600"
                            >
                              <X className="w-3 h-3" />
                              Reject
                            </button>
                          </>
                        )}
                        
                        <button
                          onClick={() => setSelectedEvent({ ...event, action: 'view' })}
                          className="flex items-center gap-1 px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-secondary"
                        >
                          <Eye className="w-3 h-3" />
                          Details
                        </button>
                        
                        <a
                          href={`/events/${event.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
                        >
                          <ExternalLink className="w-3 h-3" />
                          View
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      
      {/* Event Detail/Action Dialog */}
      <Dialog open={!!selectedEvent} onOpenChange={() => setSelectedEvent(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {selectedEvent?.action === 'reject' ? 'Reject Event' : 'Event Details'}
            </DialogTitle>
          </DialogHeader>
          
          {selectedEvent && (
            <div className="space-y-4">
              {selectedEvent.action === 'reject' ? (
                <RejectForm 
                  event={selectedEvent}
                  onReject={handleReject}
                  onCancel={() => setSelectedEvent(null)}
                  loading={actionLoading === selectedEvent.id}
                />
              ) : (
                <EventDetails 
                  event={selectedEvent}
                  onDelete={handleDelete}
                  loading={actionLoading === selectedEvent.id}
                  formatDate={formatDate}
                />
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Reject Form Component
const RejectForm = ({ event, onReject, onCancel, loading }) => {
  const [reason, setReason] = useState('');
  
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Rejecting: <strong>{event.title}</strong>
      </p>
      
      <div>
        <label className="text-sm font-medium">Reason (optional)</label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Provide a reason for rejection..."
          rows={3}
          className="mt-1 w-full px-3 py-2 border border-border rounded-lg bg-background resize-none"
        />
      </div>
      
      <div className="flex gap-2">
        <button
          onClick={() => onReject(event.id, reason)}
          disabled={loading}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
          Reject Event
        </button>
        <button
          onClick={onCancel}
          className="px-4 py-2 border border-border rounded-lg hover:bg-secondary"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

// Event Details Component
const EventDetails = ({ event, onDelete, loading, formatDate }) => (
  <div className="space-y-4">
    {/* Image */}
    {(event.image || event.images?.[0]) && (
      <img 
        src={event.image || event.images[0]} 
        alt={event.title}
        className="w-full h-48 object-cover rounded-lg"
      />
    )}
    
    {/* Info */}
    <div className="space-y-2">
      <h3 className="font-semibold text-lg">{event.title}</h3>
      <p className="text-sm text-muted-foreground">{event.description}</p>
      
      <div className="flex flex-wrap gap-4 text-sm">
        <div className="flex items-center gap-1">
          <Clock className="w-4 h-4 text-muted-foreground" />
          {formatDate(event.event_date)}
        </div>
        <div className="flex items-center gap-1">
          <MapPin className="w-4 h-4 text-muted-foreground" />
          {event.location?.venue_name || event.location?.city || event.location?.island}
        </div>
      </div>
      
      <div className="pt-2 border-t border-border">
        <p className="text-sm">
          <strong>Organizer:</strong> {event.user_name || 'Unknown'}
        </p>
        <p className="text-sm">
          <strong>Status:</strong> {event.status}
        </p>
        {event.price > 0 && (
          <p className="text-sm">
            <strong>Price:</strong> ${event.price} {event.price_currency}
          </p>
        )}
      </div>
    </div>
    
    {/* Delete Option */}
    <button
      onClick={() => onDelete(event.id)}
      disabled={loading}
      className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-500/10 text-red-500 border border-red-500/30 rounded-lg hover:bg-red-500/20"
    >
      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
      Delete Event
    </button>
  </div>
);

export default AdminEvents;
